import { useState, useEffect } from 'react'
import { isHTTPError } from 'ky'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { AlertCircle, FileSearch } from 'lucide-react'
import { api } from '@frontend/lib/api'
import type { ReportType, TriggerReviewResponse } from '@common/types'
import { REPORT_TYPES, REPORT_TYPE_LABELS } from '@common/types'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@frontend/components/ui/dialog'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'

interface RunVendorReviewModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vendorId: string
  vendorName: string
}

const GOOGLE_DRIVE_URL_REGEX = /^https:\/\/drive\.google\.com\/file\/d\/[a-zA-Z0-9_-]+/

export function RunVendorReviewModal({ open, onOpenChange, vendorId, vendorName }: RunVendorReviewModalProps) {
  const queryClient = useQueryClient()
  const [sourceUrl, setSourceUrl] = useState('')
  const [reportType, setReportType] = useState<ReportType>('soc2_type2')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setSourceUrl('')
      setReportType('soc2_type2')
      setError(null)
    }
  }, [open])

  const mutation = useMutation({
    mutationFn: (data: { sourceUrl: string; reportType: ReportType }) =>
      api.post(`vendors/${vendorId}/reviews`, { json: data }).json<TriggerReviewResponse>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] })
      queryClient.invalidateQueries({ queryKey: ['vendor-reviews', vendorId] })
      queryClient.invalidateQueries({ queryKey: ['reviews'] })
      onOpenChange(false)
      toast.success(`Review started for "${vendorName}"`)
    },
    onError: (err) => {
      if (isHTTPError(err)) {
        const data = err.data as { error?: string }
        setError(data.error || 'Failed to start review')
      } else {
        setError('Failed to start review')
      }
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!GOOGLE_DRIVE_URL_REGEX.test(sourceUrl)) {
      setError('Please enter a valid Google Drive file URL')
      return
    }

    mutation.mutate({ sourceUrl, reportType })
  }

  const isSubmitting = mutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSearch className="h-5 w-5" />
            Run Compliance Review
          </DialogTitle>
          <DialogDescription>
            Analyze a compliance report for <strong>{vendorName}</strong> using AI extraction.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="review-source-url" className="block text-sm font-medium mb-1">
              Google Drive URL <span className="text-red-500">*</span>
            </label>
            <input
              id="review-source-url"
              type="url"
              required
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="https://drive.google.com/file/d/..."
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="review-report-type" className="block text-sm font-medium mb-1">
              Report Type
            </label>
            <select
              id="review-report-type"
              value={reportType}
              onChange={(e) => setReportType(e.target.value as ReportType)}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isSubmitting}
            >
              {REPORT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {REPORT_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Starting...
                </>
              ) : (
                'Start Review'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
